import * as React from 'react';
import moment from 'moment';

import { RangeMode } from '../../models/search.model';
import { Platform, PlatformUtil } from '../../models/platform.model';
import styles from './time_range_buttons.module.css';

const PRESETS: RangeMode[] = [
  RangeMode.HOUR,
  RangeMode.DAY,
  RangeMode.WEEK,
];

interface Props {
  value: RangeMode;
  onChange: (range: RangeMode) => void;
}

const TimeRangeButtons: React.FC<Props> = ({ value, onChange }) => (
  <div className={styles.presets}>
    {PRESETS.map(preset => {
      const isActive = value === preset;
      return (
        <button
          key={preset}
          type="button"
          data-tip={isActive ? 'Clear (show max retention)' : `Last ${preset}`}
          className={`${styles.preset} ${isActive ? styles.active : ''}`}
          onClick={() => onChange(isActive ? RangeMode.ALL : preset)}>
          {preset}
        </button>
      );
    })}
  </div>
);

// Convert a RangeMode to its fromTime. ALL → epoch (server clamps to retention).
export function rangeModeToFromTime(range: RangeMode): Date {
  if (range === RangeMode.ALL) return new Date(0);
  const [, num, unit] = range.match(/^(\d+)([mhd])$/)!;
  return moment().subtract(Number(num), unit as moment.unitOfTime.DurationConstructor).toDate();
}

// Pick a histogram bucket size based on the visible duration. Matches the Logs search step ladder.
export function durationToInterval(durationMs: number): string {
  const d = moment.duration(durationMs);
  if (d.asDays() > 8) return '3h';
  if (d.asHours() > 50) return '1h';
  if (d.asHours() > 10) return '30m';
  if (d.asHours() > 3) return '10m';
  if (d.asMinutes() > 40) return '1m';
  if (d.asMinutes() > 10) return '30s';
  return '1s';
}

export function rangeModeToInterval(range: RangeMode, platform?: Platform): string {
  // ALL on the Bridge effectively shows the last 30 days of stats.
  const interval = range === RangeMode.ALL
    ? durationToInterval(30 * 24 * 60 * 60 * 1000)
    : durationToInterval(Date.now() - rangeModeToFromTime(range).getTime());
  // A server app's requests are counted per minute, so its buckets can't be finer.
  if (PlatformUtil.isServer(platform) && ['1s', '30s'].includes(interval)) return '1m';
  return interval;
}

export default TimeRangeButtons;
